// src/components/Portfolio.js
import React, { useMemo, useState, useEffect } from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

// Projects
const projects = [
  { id: 1, title: "RODRIGUES HOUSE, PUNE", area: "RESIDENTIAL  2000 sq.ft", style: "The Artistic Bohemian", desc: "Crafted for a creative soul, this home embraces bold colors, eclectic decor, handcrafted elements, and layered textures — celebrating individuality, freedom, and artistic expression.", img: "/images/i/IMG_9029.JPG"},
  { id: 2, title: "CHOPRA'S HOUSE, BANDRA", area: "RESIDENTIAL  1200 sq.ft", style: "The Urban Contemporary", desc: "Tailored for a fast-paced professional lifestyle, this apartment showcases bold accents, sleek finishes, and multi-functional spaces that reflect urban sensibilities and efficiency.", img: "/images/i/int3.jpg"},
  { id: 3, title: "SURYAVANSHI HOUSE", area: "RESIDENTIAL  2000 sq.ft", style: "The Modern Minimalist", desc: "For a young couple seeking simplicity and elegance, we created a clutter-free home with clean lines, neutral palettes, and smart storage — blending modern design with functional living.", img: "/images/i/IMG_9031.JPG"},
  { id: 4, title: "MUJAWAR VILLA", area: "RESIDENTIAL  3500 sq.ft", style: "The Nature–Inspired Retreat", desc: "A serene home designed for a nature-loving couple, , natural  light, and open layouts that bring the outdoors in — a perfect blend of calm and character", img: "/images/i/IMG_9032.JPG"},
];

const PALETTE = {
  ivory: "#F7F1E8",
  charcoal: "#593c24",
  tan: "#C3A585",
  tanHover: "#b59473",
  textDark: "#2A2A2A",
  textLight: "#FFFFFF",
  subDark: "#6C6C6C",
  subLight: "#D2D2D2",
  lineLight: "#DCD2C5",
  lineDark: "rgba(255,255,255,0.18)",
};

const serif = "font-[Playfair,serif]";

// Motion variants
const fadeSlideLeft = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
}; // slide from left [web:9]

const fadeSlideRight = {
  hidden: { opacity: 0, x: 100 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
}; // slide from right [web:9]

// Trigger when ~40% in view, only once
const sectionViewport = { once: true, amount: 0.4 }; // whileInView config [web:9]

// Safe mobile detection memoized
function useIsMobile() {
  return useMemo(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 640px)").matches;
  }, []);
}

// Hook to detect if an image is landscape using naturalWidth/Height
function useIsLandscape(src) {
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    let mounted = true;
    const img = new Image();
    img.src = src;
    img.onload = () => {
      if (!mounted) return;
      setIsLandscape(img.naturalWidth > img.naturalHeight);
    };
    return () => { mounted = false; };
  }, [src]);
  return isLandscape;
}

// Panning image that swaps renderer based on orientation
function PanningImage({ src, alt, dark, onClick }) {
  const isLandscape = useIsLandscape(src); // orientation via natural sizes [web:13]

  const baseClass = [
    "relative w-full max-w-[420px] sm:max-w-[420px] lg:max-w-[420px] aspect-[4/5] overflow-hidden",
    dark ? "soft-elev-on-dark" : "soft-elev",
    "soft-hover-pop"
  ].join(" ");

  // Common hover elevation
  const hoverProps = {
    initial: { filter: "drop-shadow(0px 0px 0px rgba(0,0,0,0))" },
    whileHover: {
      filter:
        "drop-shadow(0px 6px 14px rgba(0,0,0,0.20)) drop-shadow(0px 2px 6px rgba(0,0,0,0.10))",
      y: -1
    },
    transition: { duration: 0.22, ease: "easeOut" }
  };

  // For portrait or unknown ratio, keep <img> object-cover
  if (!isLandscape) {
    return (
      <motion.div className={baseClass} {...hoverProps}>
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover cursor-zoom-in"
          onClick={onClick}
        />
      </motion.div>
    );
  }

  // For landscape, use background-image with Motion keyframes on backgroundPositionX
  return (
    <motion.div
      className={baseClass + " cursor-zoom-in"}
      onClick={onClick}
      style={{
        backgroundImage: `url(${src})`,
        backgroundSize: "cover", // can bump to "110% auto" if edges appear
        backgroundRepeat: "no-repeat",
        backgroundPosition: "0% 50%"
      }}
      {...hoverProps}
      animate={{ backgroundPositionX: ["0%", "100%", "0%"] }} // left→right→left loop [web:9]
      transition={{ duration: 30, ease: "easeInOut", repeat: Infinity }} // calm sweep [web:9]
      aria-label={alt}
      role="img"
    />
  );
}

export default function Portfolio() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const slides = projects.map((p) => ({ src: p.img })); // lightbox slides [web:21]

  return (
    <section className="w-full pt-[65px]" style={{ backgroundColor: PALETTE.ivory }}>
      {/* Header */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
        <h1 className={`${serif} text-center tracking-[0.2em] text-3xl sm:text-5xl`} style={{ color: "rgb(89, 60, 36)" }}>
          INTERIORS
        </h1>
      </div>

      {/* Row 1 (light) */}
      <Band>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
          <Row
            number={1}
            title={projects[0].title}
            price={projects[0].area}
            bullets={[projects[0].style, projects[0].desc]}
            image={projects[0].img}
            imageSide="right"
            dark={false}
            onOpen={() => { setIndex(0); setOpen(true); }}
          />
        </div>
      </Band>

      {/* Row 2 (dark) */}
      <Band dark>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={2}
            title={projects[1].title}
            price={projects[1].area}
            bullets={[projects[1].style, projects[1].desc]}
            image={projects[1].img}
            imageSide="left"
            dark
            onOpen={() => { setIndex(1); setOpen(true); }}
          />
        </div>
      </Band>

      {/* Row 3 (light) */}
      <Band>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={3}
            title={projects[2].title}
            price={projects[2].area}
            bullets={[projects[2].style, projects[2].desc]}
            image={projects[2].img}
            imageSide="right"
            dark={false}
            onOpen={() => { setIndex(2); setOpen(true); }}
          />
        </div>
      </Band>

      {/* Row 4 (dark) */}
      <Band dark>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={4}
            title={projects[3].title}
            price={projects[3].area}
            bullets={[projects[3].style, projects[3].desc]}
            image={projects[3].img}
            imageSide="left"
            dark
            onOpen={() => { setIndex(3); setOpen(true); }}
          />
        </div>
      </Band>

      <Lightbox open={open} close={() => setOpen(false)} index={index} slides={slides} />
    </section>
  );
}

function Band({ children, dark = false }) {
  return (
    <div style={{ backgroundColor: dark ? PALETTE.charcoal : "transparent", color: dark ? PALETTE.textLight : PALETTE.textDark }}>
      {children}
    </div>
  );
}

function Row({ number, title, price, bullets, image, imageSide = "right", dark = false, onOpen }) {
  const textColor = dark ? PALETTE.textLight : PALETTE.textDark;
  const subColor = dark ? PALETTE.subLight : PALETTE.subDark;
  const lineColor = dark ? PALETTE.lineDark : PALETTE.lineLight;
  const isMobile = useIsMobile(); // mobile handling [web:9]

  // Shared image block with layered shadow + hover pop + panning for landscape
  const Image = (
    <div className="relative flex justify-center md:justify-end">
      {/* Outline frame */}
      <div
        className="absolute -top-4 -right-4 w-[79%] h-[79%] border"
        style={{ borderColor: lineColor }}
      />
      {/* Constrained portrait wrapper with soft shadow */}
      <PanningImage
        src={image}
        alt={title}
        dark={dark}
        onClick={onOpen}
      />
    </div>
  );

  // Text block with inner subgrid for number + content
  const Text = ({ side = "left" }) => {
    const numAlign = side === "right" ? "text-right" : "text-left";

    return (
      <div className="w-full">
        {/* two-col subgrid: [number | content] */}
        <div className="grid items-start gap-4 grid-cols-[90px_minmax(0,1fr)]">
          {/* Number column mirrors side */}
          <motion.div
            className={`${serif} leading-none ${numAlign}`}
            style={{ color: PALETTE.tan, fontSize: "126px", lineHeight: 0.9 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}
            viewport={sectionViewport}
          >
            {number}
          </motion.div>

          {/* Content column */}
          <motion.div
            className="flex flex-col items-start text-left max-w-[560px]"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.12 }}
            viewport={sectionViewport}
          >
            <h2 className={`${serif} md:text-4xl text-2xl`} style={{ color: textColor }}>
              {title}
            </h2>
            <div className="mt-1 h-px w-40" style={{ backgroundColor: lineColor }} />
            <p className="mt-2 md:text-2xl text-base sub" style={{ color: textColor }}>
              {price}
            </p>

            <ul className="mt-4 space-y-3">
              {bullets.map((b, i) => (
                <li key={i} className=" leading-relaxed" style={{ color: subColor }}>
                  • {b}
                </li>
              ))}
            </ul>

            <Link to="/contact" className="inline-block">
              <button
                type="button"
                className=" sub mt-6 inline-block px-6 py-2 text-sm tracking-wide"
                style={{ backgroundColor: PALETTE.tan, color: "#1B1B1B" }}
              >
                GET STARTED
              </button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  };

  // For first row on mobile: auto-visible; others scroll-triggered
  const isFirst = number === 1;
  const containerMotionProps =
    isMobile && isFirst
      ? { initial: "visible", animate: "visible" }
      : { initial: "hidden", whileInView: "visible", viewport: sectionViewport };

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 items-start gap-10"
      {...containerMotionProps}
    >
      {imageSide === "left" ? (
        <>
          {/* Image: slides from left when on left */}
          <motion.div className="order-1 md:order-1" variants={fadeSlideLeft}>
            {Image}
          </motion.div>

          {/* Text: slides from right */}
          <motion.div className="order-2 md:order-2" variants={fadeSlideRight}>
            <Text side="right" />
          </motion.div>
        </>
      ) : (
        <>
          {/* Image: slides from right when on right */}
          <motion.div className="order-1 md:order-2" variants={fadeSlideRight}>
            {Image}
          </motion.div>

          {/* Text: slides from left */}
          <motion.div className="order-2 md:order-1" variants={fadeSlideLeft}>
            <Text side="left" />
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
